import { useShallow } from 'zustand/react/shallow';
import { tutorialLevelCount } from '../game/generation/director';
import { useAppStore } from '../state/store';
import { Button, SettingsIcon, IconButton } from '../ui/components';

/**
 * Home screen: continue where the player stopped, or step sideways into level select,
 * settings and (in development builds) the generator laboratory.
 */
export function HomeScreen({
  onPlay,
  onLevels,
  onSettings,
  onLab,
}: {
  onPlay(levelNumber: number): void;
  onLevels(): void;
  onSettings(): void;
  onLab(): void;
}) {
  const { progress, storage, bootError } = useAppStore(
    useShallow((state) => ({
      progress: state.progress,
      storage: state.storage,
      bootError: state.bootError,
    })),
  );

  const nextLevel = progress.highestUnlockedLevel;
  const completed = Object.keys(progress.completions).length;
  const isFirstRun = completed === 0 && nextLevel === 1;

  return (
    <div className="ar-screen" data-variant="centred">
      <div className="ar-stack" style={{ textAlign: 'center', gap: 'var(--ar-space-5)' }}>
        <div>
          <h1 className="ar-title">Arrows</h1>
          <p className="ar-muted">
            Every arrow leaves the way it points — but only when nothing stands in its path.
          </p>
        </div>

        <div className="ar-stack">
          <Button variant="primary" size="block" onClick={() => onPlay(nextLevel)}>
            {isFirstRun ? 'Start playing' : `Continue — level ${nextLevel}`}
          </Button>
          <Button size="block" onClick={onLevels}>
            Choose a level
          </Button>
        </div>

        <div className="ar-row" style={{ justifyContent: 'center' }}>
          <IconButton label="Settings" onClick={onSettings}>
            <SettingsIcon />
          </IconButton>
          {import.meta.env.DEV && (
            <Button variant="ghost" onClick={onLab}>
              Generator lab
            </Button>
          )}
        </div>

        <p className="ar-muted">
          {completed === 0
            ? `The first ${tutorialLevelCount()} levels teach the rules.`
            : `${completed} ${completed === 1 ? 'level' : 'levels'} cleared.`}
        </p>

        {!storage.storageAvailable && (
          <p className="ar-banner" data-tone="warning" role="status">
            Progress cannot be saved in this browser session, so it will be lost when you close the
            tab. Everything else works normally.
            {bootError !== null && <span className="ar-muted"> ({bootError})</span>}
          </p>
        )}
      </div>
    </div>
  );
}
